import { Link } from 'react-router-dom';
import { Droplets, Settings } from 'lucide-react';
import { useHydrationStatus } from '../../../hooks/useHydrationStatus';
import StatusTrafficLight from './StatusTrafficLight';
import WaterButton from './WaterButton';
import Card from '../../../components/ui/Card';
import { formatTimeAgo, formatList, formatHours } from '../../../lib/format';
import { HYDRATION_STATUS } from '../../../config/constants';

/**
 * Card de hidratación en PetDetailPage.
 *
 * Muestra:
 *  - Estado del semáforo (verde/amarillo/rojo/desconocido).
 *  - "Hace X tiempo que tomaron, por <Usuario>" si lastWateredAt no es null.
 *  - Lista de pets que comparten el balde ("Compartido con: A, B").
 *  - WaterButton variant="full".
 *  - Link a /water-groups/:id/edit.
 *
 * Props:
 *  - pet: la pet desde la cual se está viendo el card (para excluirla del "compartido con").
 *  - group: el hydrationGroup completo.
 *  - sharedWith: array de pets activas que comparten balde (excluida la propia).
 *  - lastWateredByName: string | null — displayName del usuario que regó (resuelto por el caller).
 */
const HydrationCard = ({ pet, group, sharedWith = [], lastWateredByName = null }) => {
  const status = useHydrationStatus(group);

  if (!group) return null;

  const lastWateredMs = group.lastWateredAt?.toMillis?.() ?? null;
  const timeAgo = lastWateredMs ? formatTimeAgo(lastWateredMs) : null;
  const isUnknown = status.status === HYDRATION_STATUS.UNKNOWN;

  return (
    <Card>
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg bg-sky-100 p-2.5">
          <Droplets className="h-5 w-5 text-sky-700" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="font-semibold text-slate-900">Hidratación</h2>
              <p className="text-xs text-slate-500">
                Balde: <span className="font-medium">{group.name}</span> · cada{' '}
                {group.hydrationIntervalHours}h
              </p>
            </div>
            <Link
              to={`/water-groups/${group.id}/edit`}
              className="text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Editar balde"
            >
              <Settings className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <StatusTrafficLight status={status.status} variant="badge" />
        {!isUnknown && status.hoursUntilNext != null && (
          <span className="text-xs text-slate-500">
            Próxima: {formatHours(status.hoursUntilNext)}
          </span>
        )}
      </div>

      <div className="rounded-lg bg-slate-50 px-3 py-2 mb-3 text-sm text-slate-700">
        {isUnknown ? (
          <span>Todavía no se registró ninguna toma. Tocá "Dar agua" cuando refresques el balde.</span>
        ) : (
          <span>
            {timeAgo}
            {lastWateredByName ? ` · por ${lastWateredByName}` : ''}
          </span>
        )}
      </div>

      {sharedWith.length > 0 && (
        <p className="text-xs text-slate-500 mb-3">
          Compartido con: {formatList(sharedWith.map((p) => p.name))}
        </p>
      )}

      <WaterButton group={group} variant="full" />
    </Card>
  );
};

export default HydrationCard;
